// JAWS vs VoiceOver (see doc/jaws-voiceover-accessibility-reference.md):
// Upload status is announced via #aria_polite because JAWS may miss visual-only updates.
// The hidden file input is triggered by a labeled button (native pattern).

import React, { useId, useState } from 'react'
import { useData } from './DataProvider'
import useTranslation from '../shared/TranslationProvider'
import { Label } from './Label'
import { Button } from '~/lib/ui/button'
import { SpinnerWhite } from '../shared/Spinner'
import { GreenCheck, PaperClip, RedX } from './Icons'
import OnboardingFormLabel from '../SelectOnboarding/OnboardingFormLabel'
import YnsIcon from '../icons/YnsIcon'
import { useAnnouncer } from './Announcer'

const OnboardingResumeUpload = ({ label, subtext, selectOnboarding }) => {
  const { resumeName, setResumeName, resumeFileAcceptList, getApi } = useData()
  const announce = useAnnouncer()
  const resumeSubtextId = useId()

  const [resumeIsUploading, setResumeIsUploading] = useState(false)
  const [resumeUploadStatus, setResumeUploadStatus] = useState('')

  const { t } = useTranslation()

  const handleRemoveResume = () => {
    setResumeName('')
    setResumeUploadStatus('')
    announce(t('step3.resume.removed'))
  }

  const resumeChangeEvent = async (event) => {
    setResumeIsUploading(true)
    const config = { headers: { 'content-type': 'multipart/form-data' } }
    const formData = new FormData()
    formData.append('file', event.target.files[0])
    try {
      const {
        data: { name },
      } = await getApi().post('/onboarding.json', formData, config)
      setResumeIsUploading(false)
      setResumeName(name)
      setResumeUploadStatus('success')
      announce(t('step3.resume.success'))
    } catch {
      setResumeIsUploading(false)
      setResumeUploadStatus('failure')
      announce(t('step3.resume.failure'), { assertive: true })
    }
  }

  const LabelComp = selectOnboarding ? OnboardingFormLabel : Label

  return (
    <div>
      <LabelComp htmlFor="resume">{label}:</LabelComp>
      <p id={resumeSubtextId} className="pb-4 text-sm">
        {subtext}
      </p>
      <div className="flex flex-row items-center">
        <Button
          type="button"
          aria-describedby={resumeSubtextId}
          onClick={() => {
            document.getElementById('resume-uploader').click()
          }}
          disabled={resumeName || resumeIsUploading}
          className="bg-primary hover:bg-primary-darker-75 disabled:opacity-100 disabled:brightness-75"
        >
          {resumeName ? resumeName : t('step3.resume.upload')}
          {resumeIsUploading ? (
            <SpinnerWhite className="ml-2" show />
          ) : (
            <PaperClip />
          )}
        </Button>
        {resumeName && (
          <button
            type="button"
            aria-label={t('step3.resume.remove')}
            onClick={handleRemoveResume}
            className="ml-2 text-gray-500 hover:text-red-600"
          >
            <YnsIcon name="trash" aria-hidden="true" />
          </button>
        )}
        {resumeUploadStatus == 'success' && (
          <div className="flex flex-row items-center" aria-hidden="true">
            <span className="ml-4 mr-2">{t('step3.resume.success')}</span>{' '}
            <GreenCheck />
          </div>
        )}
        {resumeUploadStatus == 'failure' && (
          <div className="flex flex-row items-center" aria-hidden="true">
            <span className="ml-4 mr-2">{t('step3.resume.failure')}</span>{' '}
            <RedX />
          </div>
        )}
      </div>
      <input
        type="file"
        id="resume-uploader"
        accept={resumeFileAcceptList}
        className="sr-only"
        tabIndex={-1}
        onChange={resumeChangeEvent}
      />
    </div>
  )
}

export default OnboardingResumeUpload
